/**
 * /recruitment/requisitions — requisition admin (ADR-0012). Create + list
 * requisitions, drive the status machine, and start the open-requisition
 * approval gate (Phase 2.1): `draft → request-open → pending_open_approval`.
 * The approve/reject of a pending requisition happens in the Approvals queue
 * (the resolved manager's screen) — here HR only requests it.
 *
 * HR-gated (owner/admin/hr) — mounted under the admin layout only.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  createRequisition,
  listRequisitions,
  requestOpenRequisition,
  transitionRequisition,
  type Requisition,
  type RequisitionStatus,
} from '../api/recruitment.js';
import { listEmployees } from '../api/employees.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatDate } from '../lib/format.js';

export const recruitmentRequisitionsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/requisitions',
  component: RecruitmentRequisitionsPage,
});

/** Legal DIRECT transitions per status (mirrors REQUISITION_TRANSITIONS; the
 *  draft→open road is the approval gate, not a direct transition). */
const DIRECT_TRANSITIONS: Record<RequisitionStatus, RequisitionStatus[]> = {
  draft: ['closed'],
  pending_open_approval: [],
  open: ['on_hold', 'filled', 'closed'],
  on_hold: ['open', 'closed'],
  filled: ['closed'],
  closed: [],
};

const STATUS_LABEL: Record<RequisitionStatus, string> = {
  draft: 'Draft',
  pending_open_approval: 'Awaiting approval',
  open: 'Open',
  on_hold: 'On hold',
  filled: 'Filled',
  closed: 'Closed',
};

function RecruitmentRequisitionsPage(): JSX.Element {
  const query = useQuery({ queryKey: ['recruitment', 'requisitions'], queryFn: () => listRequisitions() });

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Requisitions</h1>
        <p className="text-sm text-gray-400">
          Open a role for hiring. Opening a requisition needs the hiring manager's
          approval — create it, then “Request open” to send it for sign-off.
        </p>
      </header>

      <CreateForm />

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load requisitions.</Placeholder>
      ) : (query.data?.requisitions ?? []).length === 0 ? (
        <Placeholder>No requisitions yet.</Placeholder>
      ) : (
        <div className="space-y-3">
          {query.data!.requisitions.map((r) => (
            <RequisitionCard key={r.id} requisition={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function CreateForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [headcount, setHeadcount] = useState('1');
  const [hiringManagerId, setHiringManagerId] = useState('');

  const managers = useQuery({ queryKey: ['employees', 'active'], queryFn: () => listEmployees({ status: 'active' }) });

  const mut = useMutation({
    mutationFn: () =>
      createRequisition({
        title: title.trim(),
        department: department.trim() || undefined,
        headcount: Number(headcount) || 1,
        hiringManagerId: hiringManagerId || undefined,
      }),
    onSuccess: async () => {
      setTitle('');
      setDepartment('');
      setHeadcount('1');
      setHiringManagerId('');
      await queryClient.invalidateQueries({ queryKey: ['recruitment', 'requisitions'] });
    },
  });

  return (
    <form
      className="rounded border border-gray-900 p-5 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) mut.mutate();
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-gray-300 space-y-1">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1">
          <span>Department</span>
          <input value={department} onChange={(e) => setDepartment(e.target.value)} aria-label="Department" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1">
          <span>Headcount</span>
          <input type="number" min="1" value={headcount} onChange={(e) => setHeadcount(e.target.value)} aria-label="Headcount" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1">
          <span>Hiring manager</span>
          <select value={hiringManagerId} onChange={(e) => setHiringManagerId(e.target.value)} aria-label="Hiring manager" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
            <option value="">— none —</option>
            {(managers.data?.employees ?? []).map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName} ({emp.employeeNumber})
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-xs text-gray-500">The hiring manager roots the open-approval — their manager (or HR) signs off.</p>
      <button type="submit" disabled={!title.trim() || mut.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
        {mut.isPending ? 'Creating…' : 'Create requisition'}
      </button>
      <ApiErrorNotice error={mut.error} fallback="Could not create the requisition." />
    </form>
  );
}

function RequisitionCard({ requisition }: { requisition: Requisition }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['recruitment', 'requisitions'] });

  const requestOpen = useMutation({ mutationFn: () => requestOpenRequisition(requisition.id), onSuccess: () => void refresh() });
  const transition = useMutation({
    mutationFn: (status: RequisitionStatus) => transitionRequisition(requisition.id, { status }),
    onSuccess: () => void refresh(),
  });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {requisition.title}
            {requisition.department ? <span className="text-gray-500 text-xs ml-2">{requisition.department}</span> : null}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {requisition.headcount} headcount · created {formatDate(requisition.createdAt)}
          </p>
        </div>
        <span className="rounded px-2 py-1 text-xs border border-gray-700 text-gray-300">{STATUS_LABEL[requisition.status]}</span>
      </header>

      <div className="flex flex-wrap gap-2">
        {requisition.status === 'draft' ? (
          <button type="button" onClick={() => requestOpen.mutate()} disabled={requestOpen.isPending} className="rounded bg-blue-700 text-white text-xs px-3 py-1 disabled:opacity-50">
            {requestOpen.isPending ? 'Requesting…' : 'Request open'}
          </button>
        ) : null}
        {requisition.status === 'pending_open_approval' ? (
          <span className="text-xs text-yellow-300">Awaiting the resolved manager’s approval (see Approvals).</span>
        ) : null}
        {DIRECT_TRANSITIONS[requisition.status].map((status) => (
          <button key={status} type="button" onClick={() => transition.mutate(status)} disabled={transition.isPending} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 disabled:opacity-50">
            {STATUS_LABEL[status]}
          </button>
        ))}
        {requisition.status === 'open' || requisition.status === 'on_hold' || requisition.status === 'filled' ? (
          <Link to="/recruitment/requisitions/$requisitionId/pipeline" params={{ requisitionId: requisition.id }} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 text-gray-200">
            Pipeline →
          </Link>
        ) : null}
      </div>
      <ApiErrorNotice error={requestOpen.error ?? transition.error} fallback="Action failed." compact />
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
